/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../../api.js";
import _ from "underscore";
import util from "../../util/util.js";
import fieldManager from "../../util/fields.js";
import { observePromise } from '../../util/ko-util';

var shorebirdSpeciesSummary = {
	name: "Shorebirds - Species Summary",
	code: "s2020_species",
	description: "For a selection of species, summarises total counts by shorebird area, state and nationally.",
	viewModel: function() {
		var self = {};

		self.type = shorebirdSpeciesSummary;

		self.startDate = ko.observable(null);
		self.endDate = ko.observable(null);
		self.selectedSpecies = ko.observable([]);
		self.biannualCountOnly = ko.observable(false);

		self.speciesAndGuilds = ko.observableArray();
		self.selectedSpecies = ko.observableArray();
		var allSpecies = [];

		self.dateRangePresets = [{
			name: 'Summer (1 Nov - 1 March)',
			startMonth: 11,
			startDay: 1,
			endMonth: 3,
			endDay: 1
		}, {
			name: 'Winter (15 May - 15 August)',
			startMonth: 5,
			startDay: 15,
			endMonth: 8,
			endDay: 15
		}];

		self.presetYear = ko.observable(new Date().getFullYear());

		self.selectedPreset = ko.observable();

		self.canGenerateReport = ko.pureComputed(function() {
			if(self.selectedPreset()) {
				var year = +self.presetYear();
				return year >= 1900 && year <= 9999;
			} else {
				return true;
			}
		});

		self.generateReport = function() {
			var params = {};

			if(self.selectedPreset()) {
				var preset = self.selectedPreset();
				var fromYear = +self.presetYear();
				var toYear = preset.startMonth > preset.endMonth ? fromYear + 1 : fromYear;

				params.from = util.formatISODate(new Date(fromYear, preset.startMonth - 1, preset.startDay));
				params.to = util.formatISODate(new Date(toYear, preset.endMonth - 1, preset.endDay));
			} else {
				if(self.startDate()) {
					params.from = util.formatISODate(self.startDate());
				}
				if(self.endDate()) {
					params.to = util.formatISODate(self.endDate());
				}
			}

			var ids = selectedSpeciesIDs();
			if(ids.length > 0) {
				params.species_ids = ids.join(",");
			}
			if(self.biannualCountOnly()) {
				params.biannual_count = true;
			}

			birdata.reportURL('s2020_species', params).then(function(url) {
				window.location = url;
			});
		}

		function speciesInGuild(guild) {
			return _.filter(allSpecies, function(sp) {
				return _.contains(sp.supertaxa, "guild:" + guild);
			});
		}

		function selectedSpeciesIDs() {
			var ids = [];
			_.each(self.selectedSpecies(), function(s) {
				if(s.id) {
					ids.push(s.id);
				} else if(s.guild) {
					ids.push(_.pluck(speciesInGuild(s.guild), 'id'));
				}
			});

			return util.uniq(ids);
		};

		Promise.all([birdata.speciesList()]).then(function([species]) {
			allSpecies = species;

			var guilds = _.chain(species)
				.map(species => species.supertaxa.filter(x => x.indexOf("guild:") == 0))
				.flatten()
				.uniq()
				.map(t => t.substr(6))
				.map(function(name) { return { name: name + " (Guild)", guild: name }; })
				.sortBy('name')
				.value();

			species = _.map(species, function(s) { return { name: s.common_name, id: s.id }; });

			self.speciesAndGuilds(guilds.concat(species));
		});

		return self;
	}
}


var shorebirdAreaSignificance = {
	name: "Shorebirds - Area Significance",
	code: "s2020_area_significance",
	description: "Identifies Shorebird Areas of national and international significance.",
	viewModel: function() {
		var self = {};

		self.type = shorebirdAreaSignificance;

		self.startDate = ko.observable(null);
		self.endDate = ko.observable(null);
		self.timeframeWindowDays = ko.observable(2);
		self.includeStateCriteria = ko.observable(true);

		self.isGeneratingReport = ko.observable(false);
		self.progressMessage = ko.observable();
		self.progress = ko.observable(0);

		self.canGenerateReport = ko.pureComputed(function() {
			return !self.isGeneratingReport();
		});

		self.generateReport = function() {
			var params = {};

			if(self.startDate()) {
				params.from = util.formatISODate(self.startDate());
			}
			if(self.endDate()) {
				params.to = util.formatISODate(self.endDate());
			}

			params.include_state_criteria = self.includeStateCriteria();
			params.timeframe_window_days = +self.timeframeWindowDays();

			self.isGeneratingReport(true);
			self.progressMessage("");
			self.progress(0);
			birdata.report('s2020_area_significance', params).then(function handleStatus(status) {
				self.progressMessage(status.value.message);
				self.progress(status.value.progress);
				if(status.value.failed) {
					return;
				} else if(status.next) {
					return status.next.then(handleStatus);
				} else {
					window.location = status.value.result_url;
				}
			}).finally(function() {
				self.isGeneratingReport(false);
			});
		}

		return self;
	}
}

var supertableReport = {
	name: "SA Department for Environment and Water - BDBSA Supertable",
	code: "sa_dew_bdbsa_supertable",
	description: "Exports surveys in BDBSA Supertable format.",
	viewModel: function() {
		var self = {};

		self.type = supertableReport;

		self.startDate = ko.observable(null);
		self.endDate = ko.observable(null);

		self.excludedSources = ko.observableArray();
		self.sources = ko.observableArray();
		birdata.lookup('source').then(self.sources);

		self.excludedPrograms = ko.observableArray();
		self.programs = ko.observableArray();
		birdata.lookup('program').then(self.programs);


		self.isGeneratingReport = ko.observable(false);
		self.progressMessage = ko.observable("One moment...");
		self.progress = ko.observable(0);

		self.canGenerateReport = ko.pureComputed(function() {
			return !self.isGeneratingReport();
		});

		self.generateReport = function() {
			var params = {};

			if(self.startDate()) {
				params.from = util.formatISODate(self.startDate());
			}
			if(self.endDate()) {
				params.to = util.formatISODate(self.endDate());
			}

			if(self.excludedSources().length > 0) {
				params.excluded_sources = self.excludedSources().map(s => s.code).join(",");
			}

			if(self.excludedPrograms().length > 0) {
				params.excluded_programs = self.excludedPrograms().map(s => s.code).join(",");
			}


			self.isGeneratingReport(true);
			birdata.reportURL('sa_dew_bdbsa_supertable', params).then(function(url) {
				window.location = url;
			}).finally(function() {
				self.isGeneratingReport(false);
			});
		}

		return self;
	}
}


var tabular = {
	name: "Tabular Report",
	code: "tabular",
	description: "Flexible reporting in a table format.",
	viewModel: function() {
		var self = {};

		self.loading = ko.observable(true);
		self.type = tabular;

		self.areAxesCompatible = function(axis1, axis2) {
			if(axis1.code == "none" || axis2.code == "none") {
				return true;
			}

			// Axes can't be the same
			if(axis1.code == axis2.code) {
				return false;
			}

			// Blacklist certain combos:
			var axes = [axis1.code, axis2.code].sort().join('|');
			switch(axes) {
				case 'species|species_supertaxon':
				case 'species|species_introduced':
				case 'area|shared_site':
					return false;
				default:
					return true;
			}
		}

		self.isResponseVariableCompatible = function(axis1, axis2, responseVariable) {
			var axisCodes = [axis1 ? axis1.code : null, axis2 ? axis2.code : null];

			if(axisCodes.indexOf('species') != -1) {
				// Has species as an axis
				if(responseVariable.code == 'unique_species' || responseVariable.code == 'average_unique_species_per_survey') {
					return false;
				}
			} else {
				// Doesn't have species an an axis
				if(responseVariable.code == 'reporting_rate') {
					return false;
				}
			}

			return true;
		}

		Promise.all([
			birdata.lookup(),
			birdata.areaLayers(),
			birdata.sharedSiteLayers(),
			birdata.regions(),
			birdata.speciesList({ supertaxa: 'all' })
		]).then(function([lookups, areaLayers, sharedSiteLayers, regions, species]) {
			self.axisTypes = [{
				name: "None",
				code: "none"
			}, {
				name: "Year",
				code: "year",
			}, {
				name: "Month",
				code: "month"
			}, {
				name: "Week of year",
				code: "week"
			}, {
				name: "Species",
				code: "species"
			}, {
				name: "Species guild",
				code: "species_supertaxon",
				supertaxonRank: "guild"
			}, {
				name: "Introduced/native species",
				code: "species_introduced"
			}, {
				name: "Species class",
				code: "species_supertaxon",
				supertaxonRank: "class"
			}, {
				name: "Survey type",
				code: "survey_type"
			}, {
				name: "Area",
				code: "area"
			}, {
				name: "Shared site",
				code: "shared_site"
			}];

			self.areaLayers = areaLayers;
			self.sharedSiteLayers = sharedSiteLayers;
			self.areaLayer = ko.observable();
			self.sharedSiteLayer = ko.observable();

			self.variableTypes = [{
				name: "Number of surveys",
				code: "number_of_surveys"
			}, {
				name: "Total count of sighted individuals",
				code: "total_count"
			}, {
				name: "Average count per survey",
				code: "average_count_per_survey"
			}, {
				name: "Reporting rate",
				code: "reporting_rate"
			}, {
				name: "Number of unique species (species richness)",
				code: "unique_species"
			}, {
				name: "Average number of unique species (species richness) per survey",
				code: "average_unique_species_per_survey"
			}, {
				name: "Last survey date",
				code: "last_survey_date"
			}, {
				name: "Number of distinct observers",
				code: "unique_observers"
			}];

			self.primaryAxis = fieldManager.field({
				label: 'Primary axis',
				name: 'axis1',
				type: 'options',
				options: self.axisTypes
			});

			self.primaryAxis(self.axisTypes[0]);

			self.secondaryAxis = fieldManager.field({
				label: 'Secondary axis',
				name: 'axis1',
				type: 'options',
				options: self.axisTypes
			});

			self.secondaryAxis(self.axisTypes[0]);

			self.variableOfInterest = fieldManager.field({
				label: 'Variable of interest',
				name: 'variableOfInterest',
				type: 'options',
				options: self.variableTypes
			});

			var program = ko.observable();

			var surveyTypes = ko.pureComputed(function() {
				if(program() && program().code == 'sop') {
					return lookups.sop_survey_type;
				} else {
					return lookups.survey_type
				}
			});

			var supertaxa = _.chain(species).pluck('supertaxa').flatten().uniq()
				.sort()
				.map(function(t) {
					return {
						name: util.capitalize(t.split(':').join(': ')),
						supertaxon: t
					}
				})
				.value();

			var speciesOptions = supertaxa.concat(species.map(function(s) {
				return { name: s.common_name, id: s.id }
			}));

			self.filters = [{
				label: 'Start date',
				name: 'from',
				type: 'date',
				to_json: x => x() ? util.formatISODate(x()) : undefined
			}, {
				label: 'End date',
				name: 'to',
				type: 'date',
				to_json: x => x() ? util.formatISODate(x()) : undefined
			}, {
				label: 'Program',
				name: 'program',
				type: 'options',
				value: program,
				options: lookups.program
			}, {
				label: 'Survey type',
				name: 'surveyType',
				type: 'options',
				options: surveyTypes
			}, {
				label: 'Breeding records only',
				name: 'breedingRecordsOnly',
				type: 'boolean'
			}, {
				label: 'Regional group',
				name: 'regionId',
				type: 'options',
				options: regions,
				to_json: x => x() ? x().id : undefined
			}, {
				label: 'Species',
				name: 'species',
				type: 'options',
				options: speciesOptions,
				save: function(json) {
					var val = this.value();
					if(val.id) { json.species_id = val.id; }
					if(val.supertaxon) { json.supertaxa = val.supertaxon; }
				}
			}].map(fieldManager.field);

			self.isGeneratingReport = ko.observable(false);
			self.progressMessage = ko.observable();
			self.progress = ko.observable(0);

			self.canGenerateReport = ko.pureComputed(function() {
				return !self.isGeneratingReport();
			});

			function hasAxis(code) {
				return [self.primaryAxis(), self.secondaryAxis()].map(a => a && a.code).includes(code);
			}

			self.reportParams = ko.pureComputed(function() {
				var params = {
					primary_axis: self.primaryAxis.field.to_json(),
					secondary_axis: self.secondaryAxis.field.to_json(),
					variable_of_interest: self.variableOfInterest.field.to_json()
				};
				self.filters.forEach(f => {
					if(f() !== undefined && f() !== null) {
						f.field.save(params);
					}
				});
				[self.primaryAxis(), self.secondaryAxis()].forEach(axis => {
					if(axis && axis.supertaxonRank) {
						params.supertaxon_rank = axis.supertaxonRank;
					}
				});
				if(self.sharedSiteLayer() && hasAxis('shared_site')) {
					params.shared_site_layer = self.sharedSiteLayer().code;
				}
				if(self.areaLayer() && hasAxis('area')) {
					params.area_layer = self.areaLayer().code;
				}
				return params;
			});

			self.reportParamsValid = ko.pureComputed(function() {
				var params = self.reportParams();
				console.log(params);
				return paramsValid(params);
			});

			function paramsValid(params) {
				return params.variable_of_interest;
			}

			self.reportObservable = ko.pureComputed(function() {
				var params = self.reportParams();
				if(paramsValid(params)) {
					return observePromise(birdata.report('tabular', params));
				} else {
					return ko.observable(null);
				}
			});

			self.reportLoading = ko.pureComputed(function() {
				var obs = self.reportObservable();
				return obs && obs.loading && obs.loading();
			});

			self.reportContent = ko.pureComputed(function() {
				var content = self.reportObservable()();
				if(content && content.data) {
					// Avoid showing too much data in the browser
					var cols = content.data[0] ? content.data[0].length : 0;
					var maxRows = Math.floor(3000 / cols);
					content.truncated = content.data.length > maxRows;

					content.axes[0].values = content.axes[0].values.slice(0, maxRows);
					content.data = content.data.slice(0, maxRows).map(
						row => row.map(
							value => value == null ? "" : util.formatNumber(value)));
				}
				return content;
			});

			self.reportError = ko.pureComputed(function() {
				let error = self.reportObservable()?.error?.()
				let message = undefined

				try {
					message = JSON.parse(error.xhr.responseText).errors?.[0]?.message
				} catch(e) {}

				if(error && message) {
					return "Error fetching data preview: " + message;
				} else if(error) {
					return "Error fetching data preview"
				}
			});

			self.download = function() {
				var params = { ...self.reportParams() };
				params.format = 'xlsx';
				birdata.reportURL('tabular', params).then(function(url) {
					window.location = url;
				});
			}

			self.loading(false);
		});

		return self;
	}
}

let abcSummary = {
	name: "Aussie Bird Count Summary",
	code: "abc_summary",
	viewModel: function() {
		var self = {};
		self.type = abcSummary;

		self.year = ko.observable("2023");

		self.canGenerateReport = ko.pureComputed(function() {
			return self.year() && +self.year() > 2000 && self.year() <= (new Date()).getFullYear();
		});

		self.generateReport = function() {
			let params = { year: self.year() };
			birdata.reportURL('abc_summary', params).then(function(url) {
				window.location = url;
			});
		};

		return self;
	}
}

let duckDB = {
	name: "DuckDB Export",
	code: "duckdb",
	viewModel: function() {
		var self = {};
		self.type = duckDB;
		self.info = ko.observable();
		self.status = ko.observable('loading');
		birdata.report("duckdb").then(function(info) {
			let fmt = new Intl.DateTimeFormat('en-AU', {
				dateStyle: 'short',
				timeStyle: 'short'
			})
			info.download_expiry_formatted = fmt.format(Date.parse(info.download_expiry))
			info.last_updated_formatted = fmt.format(Date.parse(info.last_updated))
			self.info(info);
			self.status('loaded');
		}).catch(function(error) {
			console.log(error);
			self.status('error');
		});
		return self;
	}
}

let obpResight = {
	name: "OBP Resight Spreadsheet",
	code: "obp_resight",
	viewModel: function() {
		return {
			startDate: ko.observable(null),
			endDate: ko.observable(null),
			type: obpResight,
			generateReport() {
				let params = {
					'start_date': this.startDate() ? util.formatISODate(this.startDate()) : undefined,
					'end_date': this.endDate() ? util.formatISODate(this.endDate()) : undefined
				}

				birdata.reportURL("obp_resight", params).then(url => window.location = url);
			}
		}
	}
}

function ReportsViewModel(params) {
	var self = this;

	self.loaded = ko.observable(false);
	self.errorMessage = ko.observable();

	self.reportTypes = [
		shorebirdSpeciesSummary,
		shorebirdAreaSignificance,
		tabular,
		supertableReport,
		abcSummary,
		duckDB,
		obpResight
	];
	self.reportType = ko.observable(null);

	self.report = ko.observable(null);

	self.reportType.subscribe(function(type) {
		if(type) {
			self.report(type.viewModel());
		} else {
			self.report(null);
		}
	});

	Promise.all([birdata.speciesList()]).then(function([species]) {
		self.species = ko.observable(species);

		self.loaded(true);
	}).catch(function(e) {
		if(e.xhr && e.xhr.status == 403) {
			self.errorMessage("Access Denied");
		}
	});
}

ko.components.register('reports', {
	viewModel: function(params) {
		return new ReportsViewModel(params);
	},
	template: require('../../../templates/admin/reports.html')
});
